import React, { FormEvent } from 'react';
import { Head, useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { PhoneInput } from '../../components/PhoneInput';
import { Phone, User } from 'lucide-react';

interface UserData {
  id: number;
  name: string;
  email: string;
}

interface OnboardingProps {
  user: UserData;
  errors?: string[];
}

const Onboarding: React.FC<OnboardingProps> = ({ user, errors = [] }) => {
  const { data, setData, post, processing, errors: formErrors } = useForm({
    name: user.name || '',
    phone: '',
    intent: '',
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    post('/onboarding');
  }

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Complete Your Profile
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Tell us a bit about yourself to get started.
          </p>
        </div>

        {/* Form Card */}
        <Card className="p-5 sm:p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                <ul className="list-disc list-inside">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Name Field - Pre-filled from user */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                placeholder="Enter your full name"
                className="h-9"
                required
              />
              {formErrors.name && (
                <p className="text-sm text-destructive">{formErrors.name}</p>
              )}
            </div>

            {/* Phone Number (Optional) */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                Phone Number <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <PhoneInput
                id="phone"
                name="phone"
                value={data.phone}
                onChange={(value) => setData('phone', value || '')}
                placeholder="Enter your phone number"
                defaultCountry="US"
                className="w-full"
              />
              {formErrors.phone && (
                <p className="text-sm text-destructive">{formErrors.phone}</p>
              )}
            </div>

            {/* User Intent */}
            <div className="flex flex-col space-y-2">
              <Label htmlFor="intent" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                What brings you here?
              </Label>
              <Textarea
                id="intent"
                name="intent"
                value={data.intent}
                onChange={(e) => setData('intent', e.target.value)}
                placeholder="Tell us what you're looking for..."
                className="h-20"
                rows={3}
              />
              {formErrors.intent && (
                <p className="text-sm text-destructive">{formErrors.intent}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
              >
                {processing ? 'Saving...' : 'Complete Profile'}
              </Button>
            </div>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs sm:text-sm text-muted-foreground">
          <p>This helps us personalize your experience.</p>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
